import { useState, type ComponentType, type SVGProps } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { routes } from "@/app/navigation/routes";
import {
  ChevronRightIcon,
  CreditCardIcon,
  GearIcon,
  LogoutIcon,
  MessageCircleIcon,
  TrophyIcon,
  UserIcon,
} from "@/assets/svg";
import { SettingsLogoutModal } from "@/features/settings";
import settingsHouseImg from "@/assets/images/settings-house.png";
import styles from "./SettingsLayout.module.css";

type SettingsNavItem = {
  label: string;
  shortLabel?: string;
  to?: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  onClick?: () => void;
};

const HouseOutlineIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    <polyline points="9 22 9 12 15 12 15 22" />
  </svg>
);

const MenuListIcon = (props: SVGProps<SVGSVGElement>) => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);

const accountSection: SettingsNavItem[] = [
  { label: "Account", shortLabel: "Account", to: routes.settingsAccount, icon: UserIcon },
];

export const SettingsLayout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLogoutModalOpen, setIsLogoutModalOpen] = useState(false);
  const location = useLocation();

  const handleOpenLogout = () => {
    setMobileMenuOpen(false);
    setIsLogoutModalOpen(true);
  };

  const othersSection: SettingsNavItem[] = [
    {
      label: "Protest History",
      shortLabel: "Protest History",
      to: routes.settingsProtestHistory,
      icon: HouseOutlineIcon,
    },
    {
      label: "Autopay Subscription",
      shortLabel: "Autopay",
      to: routes.settingsAutopaySubscription,
      icon: CreditCardIcon,
    },
    {
      label: "Historical Achievements",
      shortLabel: "Achievements",
      to: routes.settingsHistoricalAchievements,
      icon: TrophyIcon,
    },
    {
      label: "App Settings",
      shortLabel: "Settings",
      to: routes.settingsAppSettings,
      icon: GearIcon,
    },
    {
      label: "Contact Us",
      shortLabel: "Contact",
      to: routes.settingsContactUs,
      icon: MessageCircleIcon,
    },
    {
      label: "Logout",
      shortLabel: "Logout",
      icon: LogoutIcon,
      onClick: handleOpenLogout,
    },
  ];

  const allNavItems: SettingsNavItem[] = [...accountSection, ...othersSection];

  const renderDesktopNavItem = (item: SettingsNavItem) => {
    const Icon = item.icon;

    if (item.onClick) {
      return (
        <button
          key={item.label}
          type="button"
          onClick={item.onClick}
          className={`${styles.navItem} ${styles.navButton}`}
        >
          <div className={styles.navItemLeft}>
            <Icon className={styles.navIcon} aria-hidden="true" />
            <span>{item.label}</span>
          </div>
          <ChevronRightIcon className={styles.navChevron} aria-hidden="true" />
        </button>
      );
    }

    return (
      <NavLink
        key={item.to}
        to={item.to!}
        className={({ isActive }) =>
          `${styles.navItem} ${isActive ? styles.activeNavItem : ""}`.trim()
        }
      >
        <div className={styles.navItemLeft}>
          <Icon className={styles.navIcon} aria-hidden="true" />
          <span>{item.label}</span>
        </div>
        <ChevronRightIcon className={styles.navChevron} aria-hidden="true" />
      </NavLink>
    );
  };

  const renderMobilePill = (item: SettingsNavItem) => {
    const Icon = item.icon;

    if (item.onClick) {
      return (
        <button
          key={`pill-${item.label}`}
          type="button"
          onClick={item.onClick}
          className={`${styles.mobilePill} ${styles.mobilePillButton}`}
        >
          <Icon className={styles.mobilePillIcon} aria-hidden="true" />
          <span>{item.shortLabel || item.label}</span>
        </button>
      );
    }

    const isActive = item.to ? location.pathname.startsWith(item.to) : false;

    return (
      <NavLink
        key={`pill-${item.to}`}
        to={item.to!}
        onClick={() => setMobileMenuOpen(false)}
        className={`${styles.mobilePill} ${isActive ? styles.activeMobilePill : ""}`.trim()}
      >
        <Icon className={styles.mobilePillIcon} aria-hidden="true" />
        <span>{item.shortLabel || item.label}</span>
      </NavLink>
    );
  };

  return (
    <div className={styles.settingsPage}>
      <div className={styles.settingsShell}>
        {/* ================================================================= */}
        {/* Desktop Sidebar (visible on screens > 1024px)                     */}
        {/* ================================================================= */}
        <aside className={styles.sidebar}>
          <div className={styles.sidebarHeader}>
            <h2 className={styles.sidebarTitle}>Account Settings</h2>
            <p className={styles.sidebarSubtitle}>Manage your account and preferences</p>
          </div>

          <div className={styles.sectionLabel}>ACCOUNT SETTINGS</div>
          <nav className={styles.navList}>{accountSection.map(renderDesktopNavItem)}</nav>

          <div className={styles.sectionLabel}>OTHERS</div>
          <nav className={styles.navList}>{othersSection.map(renderDesktopNavItem)}</nav>

          <div className={styles.sidebarHouseWrap}>
            <img src={settingsHouseImg} alt="" className={styles.sidebarHouseImg} />
          </div>
        </aside>

        {/* ================================================================= */}
        {/* Mobile & Tablet Navigation Bar (visible on screens <= 1024px)     */}
        {/* ================================================================= */}
        <div className={styles.mobileNavCard}>
          <div className={styles.mobileHeaderRow}>
            <div>
              <h2 className={styles.mobileTitle}>Account Settings</h2>
              <p className={styles.mobileSubtitle}>Manage your account and preferences</p>
            </div>
            <button
              type="button"
              className={`${styles.mobileMenuToggle} ${
                mobileMenuOpen ? styles.mobileMenuToggleActive : ""
              }`}
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-expanded={mobileMenuOpen}
              aria-label="Toggle settings navigation menu"
            >
              <MenuListIcon />
              <span>{mobileMenuOpen ? "Close" : "All Menu"}</span>
            </button>
          </div>

          {/* Quick horizontal scrollable tabs */}
          <nav className={styles.mobilePillsScroll} aria-label="Settings sections tabs">
            {allNavItems.map(renderMobilePill)}
          </nav>

          {/* Collapsible expanded list when toggled */}
          {mobileMenuOpen && (
            <div className={styles.mobileExpandedMenu}>
              <div className={styles.sectionLabel}>ACCOUNT SETTINGS</div>
              <nav className={styles.navList}>{accountSection.map(renderDesktopNavItem)}</nav>

              <div className={styles.sectionLabel}>OTHERS</div>
              <nav className={styles.navList}>{othersSection.map(renderDesktopNavItem)}</nav>
            </div>
          )}
        </div>

        {/* Main Content Area */}
        <section className={styles.content}>
          <Outlet />
        </section>
      </div>

      <SettingsLogoutModal isOpen={isLogoutModalOpen} onClose={() => setIsLogoutModalOpen(false)} />
    </div>
  );
};
